/**
 * Sing4YouCafePage — Host / Audience monitoring page
 *
 * B7 FIX — VideoTracker had onNetworkError prop — caused constant remounts
 *   OLD: VideoTracker called onNetworkError() (= bumpRevision) whenever
 *        track.play() threw. Any layout visibility event (tab blur, scroll,
 *        window resize) could trigger track.play() to throw, incrementing
 *        streamKey and causing Sing4YouLiveStream to fully remount. This
 *        felt like the page constantly needing a "reload".
 *   FIX: VideoTracker has NO onNetworkError prop. It only ever calls
 *        track.play() to recover visibility. It NEVER bumps streamKey.
 *        Only Sing4YouLiveStream's own connection-state-change listener
 *        (true DISCONNECTED + NETWORK_ERROR) should bump streamKey.
 *
 * B8 FIX — state_request sent 800ms after RTM join — race with Agora RTC
 *   OLD: On slow connections, Agora RTC join (in Sing4YouLiveStream) could
 *        take >800ms. The talent received state_request and sent pvlc_load +
 *        pvlc_sync via RTM before the viewer's video pipeline was ready.
 *        The PVLC badge appeared but the canvas video never played.
 *   FIX: Delay raised from 800ms → 1500ms. This gives the RTC session
 *        enough time to join and negotiate before the state snapshot arrives.
 *
 * B9 FIX (NEW) — enqueueSnackbar in setupRTM deps caused RTM reconnect loop
 *   OLD: setupRTM useCallback deps included enqueueSnackbar. notistack does
 *        not guarantee a stable reference — it changes on context updates,
 *        including when a snackbar appears. Every snackbar triggered setupRTM
 *        to recreate, its useEffect re-ran, RTM tore down and tried to log in
 *        again with the same viewerUserId. Agora RTM rejected the duplicate
 *        login with error code 5, which showed another snackbar → infinite loop.
 *   FIX: enqueueSnackbar removed from setupRTM deps. All snackbar calls inside
 *        setupRTM use the module-level import directly (not a closure dep).
 *        eslint-disable comment added to suppress the exhaustive-deps warning.
 *
 * ALL OTHER PRODUCTION FIXES UNCHANGED:
 *   A. VideoTracker calls track.play() for layout-visibility events (SIZE/STYLE
 *      /POSITION/COVERED) — no session teardown.
 *   B. streamKey debounced 500ms — rapid-fire NETWORK_ERROR triggers collapse.
 *   C. RTM reconnect with exponential backoff (3s → 6s → 12s → 24s → 48s).
 *   D. RTM token fetched via getAgoraRtmToken — supports Agora Console token auth.
 *   PVLC state stored in useRef (pvlcRef) so it survives streamKey remounts.
 *   pvlcRenderTick forces re-render when pvlcRef changes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRemoteUsers, useRemoteVideoTracks } from "agora-rtc-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ContentCopy as ContentCopyIcon, Check as CheckIcon, ChatBubbleOutline, ChevronRight, Visibility } from "@mui/icons-material";
import { Dialog, IconButton, Tooltip } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import AgoraRTM from "agora-rtm-sdk";
import { enqueueSnackbar } from "notistack";

import { useAuth } from "../../../hooks";
import { AGORA_RTM_APP_ID } from "../../../constants/config";
import { getAgoraRtmToken } from "../../../services/api/agora.api";
import { useSendReaction, ReactionType } from "../../../services/stagemode_api/viewers/viewers_api";
import AgoraWrapper from "../Karaokecard/AgoraWrapper";
import Sing4YouLiveStream from "./Sing4YouLiveStream_bug_fixed_PVLC";
import KaraokeLyrics from "../Karaokecard/Agora/KaraokeLyrics";
import { useLyricsAutoFetch } from "./Lyrics/useLyricsautofetch";
import LiveChat from "../LiveChat";
import GuestManagement from "../Talent/GuestManagement";

import "../../../styles/sing4you-live.css";

/* ── Reaction buttons ────────────────────────────────────────────────────── */
const REACTION_MAP: { emoji: string; type: ReactionType; label: string }[] = [
  { emoji: "🔥", type: "Like",  label: "Fire"    },
  { emoji: "❤️", type: "Heart", label: "Love"    },
  { emoji: "🎤", type: "Clap",  label: "Applaud" },
  { emoji: "⭐", type: "Star",  label: "Star"    },
  { emoji: "🎵", type: "Like",  label: "Vibe"    },
  { emoji: "🎶", type: "Clap",  label: "Music"   },
  { emoji: "🎼", type: "Like",  label: "Tune"    },
  { emoji: "💕", type: "Heart", label: "Hearts"  },
];

type SidebarTab = "chat" | "guests" | "qr";

/* ── VideoTracker ─────────────────────────────────────────────────────────
 * B7 FIX: No onNetworkError prop. This component ONLY calls track.play()
 * to recover layout-based visibility. It NEVER bumps streamKey.
 * ─────────────────────────────────────────────────────────────────────── */
function VideoTracker() {
  const remoteUsers = useRemoteUsers();
  const { videoTracks } = useRemoteVideoTracks(remoteUsers);

  useEffect(() => {
    if (!videoTracks.length) return;
    const cleanups: (() => void)[] = [];
    const LAYOUT_REASONS = ["SIZE", "STYLE", "POSITION", "COVERED"];

    videoTracks.forEach((track) => {
      const current = (track as any).getVideoElementVisibleStatus?.() as
        | { visible: boolean; reason?: string } | undefined;
      if (current && !current.visible && LAYOUT_REASONS.includes(current.reason ?? "")) {
        try { (track as any).play?.(); } catch {}
      }

      const handler = ({ visible, reason }: { visible: boolean; reason?: string }) => {
        if (!visible && LAYOUT_REASONS.includes(reason ?? "")) {
          try {
            (track as any).play?.();
          } catch (err) {
            console.warn("[VideoTracker] track.play() failed for layout reason:", reason, err);
          }
        }
      };

      (track as any).on("video-element-visible-status", handler);
      cleanups.push(() => (track as any).off("video-element-visible-status", handler));
    });

    return () => cleanups.forEach((fn) => fn());
  }, [videoTracks]);

  return null;
}

/* ── Page ─────────────────────────────────────────────────────────────────── */
export function Sing4YouCafePage() {
  const { channel_name = "" } = useParams();
  const navigate              = useNavigate();
  const { userInfo }          = useAuth();
  const [params]              = useSearchParams();
  const bookingId             = params.get("bookingId") ?? "";

  const [showWelcome,   setShowWelcome]   = useState(true);
  const [activeTab,     setActiveTab]     = useState<SidebarTab>("chat");
  const [sidebarOpen,   setSidebarOpen]   = useState(true);
  const [copied,        setCopied]        = useState(false);
  const [viewerCount,   setViewerCount]   = useState(0);
  const [lastSentEmoji, setLastSentEmoji] = useState<string | null>(null);
  const [isFullscreen,  setIsFullscreen]  = useState(false);

  /* ── streamKey — ONLY bumped by true NETWORK_ERROR ───────────────────── */
  const [streamKey,       setStreamKey]       = useState(0);
  const bumpRevisionTimer                      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bumpRevision = useCallback(() => {
    if (bumpRevisionTimer.current) return;
    bumpRevisionTimer.current = setTimeout(() => {
      setStreamKey((k) => k + 1);
      bumpRevisionTimer.current = null;
    }, 500);
  }, []);

  /* ── Karaoke state ────────────────────────────────────────────────────── */
  // hostLyricsLrc removed — LRC now fetched from LRCLIB via useLyricsAutoFetch
  // audienceLrc (derived below from lyricsResult) is used in render instead.
  const [hostLyricsTime, setHostLyricsTime] = useState(0);
  const [hostIsPlaying,  setHostIsPlaying]  = useState(false);
  const [hostSongTitle,  setHostSongTitle]  = useState("");
  const [hostDuration,   setHostDuration]   = useState(0);

  /* ── PVLC state in useRef — survives streamKey remounts ──────────────── */
  const pvlcRef = useRef({ active: false, title: "", time: 0, playing: false, duration: 0 });
  const [pvlcRenderTick, setPvlcRenderTick] = useState(0);
  const tickPvlc = useCallback(() => setPvlcRenderTick((n) => n + 1), []);

  // Dedupe LRCLIB fetch calls (talent may send karaoke_load twice: immediate + retry)
  const lastLyricsQueryRef = useRef<string>("");

  /* ── Lyrics auto-fetch from LRCLIB ────────────────────────────────────────
   * When talent plays a song, RTM karaoke_load sends { title, artist } only.
   * The audience side fetches the LRC independently from LRCLIB using the
   * same hook as the talent page. This avoids sending the full LRC string
   * (potentially 20–50KB) over RTM which has a 32KB message size limit.
   *
   * Flow:
   *   Talent uploads MP3 → auto-fetch LRC → plays song
   *   RTM karaoke_load: { type, title, artist, duration }   ← no lrc field
   *   Audience receives title+artist → fetchForFilename triggers LRCLIB
   *   LRCLIB returns synced LRC → KaraokeLyrics renders on audience screen
   *
   * karaoke_sync still carries currentTime + playing state for live sync.
   * ─────────────────────────────────────────────────────────────────────── */
  const {
    result:               lyricsResult,
    fetchForFilename:     fetchLyricsForFilename,
    fetchForTitleArtist:  fetchLyricsForAudience,  // use title+artist directly (no filename cleaning)
    reset:                resetAudienceLyrics,
  } = useLyricsAutoFetch();

  // Derived LRC — use LRCLIB result when available, otherwise empty
  const audienceLrc = (lyricsResult.status === "found" || lyricsResult.status === "plain")
    ? lyricsResult.lrc
    : "";

  const rtmClientRef   = useRef<ReturnType<typeof AgoraRTM.createInstance> | null>(null);
  const rtmChannelRef  = useRef<ReturnType<ReturnType<typeof AgoraRTM.createInstance>["createChannel"]> | null>(null);
  const rtmMountedRef  = useRef(false);
  const rtmRetryCount  = useRef(0);
  const rtmRetryTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fullscreenRef  = useRef<HTMLDivElement>(null);

  const joinUrl = useMemo(
    () => `https://stagelink.live/sing4you-interaction/${channel_name}?bookingId=${bookingId}`,
    [channel_name, bookingId],
  );

  // Agora RTM requires a purely numeric UID string — no letters or underscores.
  // We combine the user's numeric ID with the last 7 digits of Date.now()
  // to produce a unique numeric string per session.
  // e.g. userId=62, timestamp suffix=8220013 → "628220013"
  const viewerUserId = useMemo(() => {
    const numericId = String(userInfo?.id ?? "").replace(/\D/g, "") || "0";
    const suffix    = String(Date.now()).slice(-7);
    return `${numericId}${suffix}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync: sendReaction } = useSendReaction(bookingId);
  const handleCallEnd = useCallback(() => navigate("/"), [navigate]);

  const handleReact = useCallback(async (emoji: string, type: ReactionType) => {
    try {
      setLastSentEmoji(emoji);
      setTimeout(() => setLastSentEmoji(null), 420);
      await sendReaction({ type, status: 1 });
      rtmChannelRef.current?.sendMessage({ text: JSON.stringify({ type: "reaction", emoji }) }).catch(() => {});
    } catch {}
  }, [sendReaction]);

  const handleCopy = useCallback(async () => {
    try { await navigator.clipboard.writeText(joinUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  }, [joinUrl]);

  /* ── Fullscreen ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) document.exitFullscreen().catch(() => {});
    else fullscreenRef.current?.requestFullscreen().catch(() => {});
  }, [isFullscreen]);

  /* ── RTM setup ─────────────────────────────────────────────────────────── */
  const setupRTM = useCallback(async () => {
    if (!AGORA_RTM_APP_ID || !channel_name) return;
    try {
      await rtmChannelRef.current?.leave().catch(() => {});
      await rtmClientRef.current?.logout().catch(() => {});
      rtmChannelRef.current = null;
      rtmClientRef.current  = null;

      // B9 FIX: enqueueSnackbar is NOT in deps (see comment at top of file).
      // It is used here via module-level import — not as a closure variable —
      // so removing it from deps is safe and does not cause a stale reference.
      const rtmToken = await getAgoraRtmToken(viewerUserId).catch(() => null);
      const client = AgoraRTM.createInstance(AGORA_RTM_APP_ID);
      try {
        await client.login({ uid: viewerUserId, token: rtmToken ?? undefined });
      } catch (loginErr: any) {
        const code = loginErr?.code ?? loginErr?.errorCode;
        if (code === 5) {
          // RTM token auth is ON in Agora Console but token fetch failed or
          // returned null. Fix: implement getAgoraRtmToken on your backend,
          // or disable RTM token auth in Agora Console → Project → Edit → RTM.
          enqueueSnackbar("Chat sync requires secure login. Please reload or try again.", { variant: "warning", autoHideDuration: 6000 });
        } else {
          enqueueSnackbar("Could not connect to chat. Reload to try again.", { variant: "warning", autoHideDuration: 5000 });
        }
        throw loginErr;
      }
      if (!rtmMountedRef.current) { client.logout().catch(() => {}); return; }

      const ch = client.createChannel(channel_name);

      ch.on("ChannelMessage", (message: any) => {
        if (!rtmMountedRef.current) return;
        try {
          const msg = JSON.parse(message.text ?? "{}");
          if (msg.type === "karaoke_track") {
            // Fallback: talent may only know filename at upload/play-start time.
            // Start LRCLIB search immediately using the same filename cleaner
            // as the talent page. When karaoke_load arrives later (title+artist),
            // that higher-quality lookup will replace this.
            const filename = String(msg.filename ?? "").trim();
            if (filename) {
              setHostSongTitle(filename);
              setHostDuration(msg.duration ?? 0);
              setHostLyricsTime(msg.time ?? 0);
              const q = `file||${filename}`;
              if (lastLyricsQueryRef.current !== q) {
                lastLyricsQueryRef.current = q;
                resetAudienceLyrics();
                fetchLyricsForFilename(filename);
              }
            }
          } else if (msg.type === "karaoke_load") {
            // New: talent sends title + artist, audience fetches LRC from LRCLIB
            // The full LRC string is no longer sent over RTM (avoids 32KB limit).
            const title  = String(msg.title  ?? "");
            const artist = String(msg.artist ?? "");
            setHostSongTitle(title && artist ? `${title} — ${artist}` : title || artist);
            setHostDuration(msg.duration ?? 0);
            setHostLyricsTime(msg.time ?? 0);
            // FIX: pass title+artist directly to fetchForTitleArtist.
            // This runs structured LRCLIB search first (most accurate) then
            // flat query fallback — no filename cleaning needed since we
            // already have clean metadata from the talent side.
            if (title) {
              const q = `${title}||${artist}`.trim();
              if (lastLyricsQueryRef.current !== q) {
                lastLyricsQueryRef.current = q;
                resetAudienceLyrics();
                fetchLyricsForAudience(title, artist);
              }
            }
          } else if (msg.type === "karaoke_sync") {
            setHostLyricsTime(msg.time ?? 0);
            setHostIsPlaying(!!msg.playing);
          } else if (msg.type === "karaoke_end") {
            setHostSongTitle("");
            setHostDuration(0);
            setHostLyricsTime(0);
            setHostIsPlaying(false);
            lastLyricsQueryRef.current = "";
            resetAudienceLyrics();
          } else if (msg.type === "pvlc_load") {
            pvlcRef.current.active   = true;
            pvlcRef.current.title    = msg.title    ?? "";
            pvlcRef.current.duration = msg.duration ?? 0;
            tickPvlc();
          } else if (msg.type === "pvlc_sync") {
            pvlcRef.current.time     = msg.time     ?? 0;
            pvlcRef.current.playing  = msg.playing  ?? false;
            pvlcRef.current.duration = msg.duration ?? 0;
            tickPvlc();
          } else if (msg.type === "pvlc_end") {
            pvlcRef.current = { active: false, title: "", time: 0, playing: false, duration: 0 };
            tickPvlc();
          }
        } catch {}
      });

      await ch.join();
      rtmRetryCount.current = 0;

      // B8 FIX: 1500ms delay so RTC has time to join before state snapshot arrives
      setTimeout(() => {
        if (!rtmMountedRef.current) return;
        ch.sendMessage({ text: JSON.stringify({ type: "state_request" }) }).catch(() => {});
      }, 1500);

      const updateCount = async () => {
        try { const m = await ch.getMembers(); if (rtmMountedRef.current) setViewerCount(m.length); } catch {}
      };
      ch.on("MemberJoined", updateCount);
      ch.on("MemberLeft",   updateCount);
      updateCount();

      // Exponential backoff reconnect: 3s → 6s → 12s → 24s → 48s
      client.on("ConnectionStateChanged", (newState: string) => {
        if (newState === "DISCONNECTED" && rtmMountedRef.current) {
          if (rtmRetryCount.current >= 5) {
            enqueueSnackbar("⚠️ Chat sync disconnected — reload to restore", { variant: "warning", autoHideDuration: 6000 });
            return;
          }
          const delay = 3000 * Math.pow(2, rtmRetryCount.current);
          rtmRetryCount.current += 1;
          rtmRetryTimer.current = setTimeout(() => { if (rtmMountedRef.current) setupRTM(); }, delay);
        }
      });

      rtmClientRef.current  = client;
      rtmChannelRef.current = ch;
    } catch (err) {
      console.warn("[Sing4You Cafe RTM]", err);
    }
    // B9 FIX: enqueueSnackbar intentionally excluded — it is not a stable
    // reference from notistack and would cause RTM to reconnect on every render,
    // triggering duplicate login rejections (RTM error code 5) in a loop.
  }, [viewerUserId, channel_name, tickPvlc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    rtmMountedRef.current = true;
    setupRTM();
    return () => {
      rtmMountedRef.current = false;
      if (rtmRetryTimer.current) clearTimeout(rtmRetryTimer.current);
      rtmChannelRef.current?.leave().catch(() => {});
      rtmClientRef.current?.logout().catch(() => {});
    };
  }, [setupRTM]);

  /* ── Local clock interpolation ────────────────────────────────────────── */
  useEffect(() => {
    if (!hostIsPlaying) return;
    let lastTick = Date.now();
    const id = setInterval(() => {
      const now   = Date.now();
      const delta = (now - lastTick) / 1000;
      lastTick    = now;
      setHostLyricsTime((t) => hostDuration > 0 && t >= hostDuration ? t : t + delta);
    }, 100);
    return () => clearInterval(id);
  }, [hostIsPlaying, hostDuration]);

  /* ── Socket: pre-load LRC ─────────────────────────────────────────────────
   * REMOVED — lyrics are now fetched from LRCLIB via useLyricsAutoFetch when
   * the RTM karaoke_load message arrives with { title, artist }.
   * The Socket subscription for lyrics is no longer needed on the audience side.
   * ─────────────────────────────────────────────────────────────────────── */

  /* ─────────────────────────────── RENDER ─────────────────────────────── */
  const pvlc = pvlcRef.current;

  return (
    <>
      <div className="s4y-page" ref={fullscreenRef}>

        <header className="s4y-header">
          <div className="s4y-logo">
            <span className="s4y-logo-icon">🎤</span>
            <div className="s4y-logo-text">
              <span className="s4y-logo-title">SING4YOU</span>
              <span className="s4y-logo-sub">Live Karaoke</span>
            </div>
          </div>
          <div className="s4y-role-badge">👁&nbsp;&nbsp;HOST VIEW</div>
          <div className="s4y-header-right">
            <Tooltip title={sidebarOpen ? "Hide Chat & panel" : "Show Chat & panel"}>
              <button
                type="button"
                className="s4y-tools-btn"
                onClick={() => setSidebarOpen((o) => !o)}
                style={{ marginRight: 4 }}
              >
                <ChatBubbleOutline sx={{ fontSize: "0.9rem" }} />
                {sidebarOpen ? "Hide panel" : "Chat"}
              </button>
            </Tooltip>
            <span className="s4y-live-pill"><span className="s4y-live-dot" />LIVE</span>
            <span className="s4y-viewer-count"><Visibility sx={{ fontSize: "0.9rem", opacity: 0.65 }} />{viewerCount}</span>
          </div>
        </header>

        <div className="s4y-body" style={{ position: "relative" }}>
          <main className="s4y-main">
            <div className="s4y-video-area s4y-video-area--full">
              <div className="s4y-video-inner">
                {/*
                 * AgoraWrapper is here — inside the render, wrapping only
                 * VideoTracker + Sing4YouLiveStream. There is NO outer
                 * AgoraWrapper at the export level. A second wrapper would
                 * create two Agora RTC clients with the same uid, causing
                 * CAN_NOT_GET_GATEWAY_SERVER on the second join attempt.
                 */}
                <AgoraWrapper mode="live" role="audience">
                  <VideoTracker />
                  <Sing4YouLiveStream
                    key={streamKey}
                    userId={viewerUserId}
                    videoId={channel_name}
                    displayName="Host"
                    onCallEnd={handleCallEnd}
                    onNetworkError={bumpRevision}
                    qrCodeClickHandler={() => setActiveTab("qr")}
                    karaokeActive={hostIsPlaying}
                    karaokeTrackName={hostSongTitle}
                    pvlcActive={pvlc.active}
                    pvlcTitle={pvlc.title}
                    pvlcTime={pvlc.time}
                    pvlcPlaying={pvlc.playing}
                    pvlcDuration={pvlc.duration}
                    isFullscreen={isFullscreen}
                    onToggleFullscreen={toggleFullscreen}
                  />
                </AgoraWrapper>
              </div>
            </div>

            {/* Lyrics fetched from LRCLIB when talent starts a song.
                audienceLrc is populated by useLyricsAutoFetch after receiving
                karaoke_load RTM message with { title, artist } from talent. */}
            {audienceLrc && (
              <KaraokeLyrics
                lrc={audienceLrc}
                currentTime={hostLyricsTime}
                duration={hostDuration}
                songTitle={hostSongTitle || undefined}
                isPlaying={hostIsPlaying}
              />
            )}
            {/* Show a subtle searching indicator while LRCLIB is fetching */}
            {!audienceLrc && lyricsResult.status === "searching" && hostSongTitle && (
              <div style={{
                textAlign: "center", padding: "8px 0",
                color: "rgba(255,255,255,0.35)", fontSize: "0.72rem",
              }}>
                🎵 Loading lyrics for {hostSongTitle}…
              </div>
            )}
          </main>

          {/* Collapsed sidebar re-open pill */}
          {!sidebarOpen && (
            <Tooltip title="Open Chat & panel">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                style={{
                  position: "absolute", top: "50%", right: 0,
                  transform: "translateY(-50%)",
                  writingMode: "vertical-rl", textTransform: "uppercase",
                  letterSpacing: "0.1em", fontSize: "0.7rem", fontWeight: 700,
                  color: "rgba(255,255,255,0.85)",
                  background: "rgba(15,23,42,0.95)",
                  border: "1px solid rgba(148,163,184,0.4)", borderRight: "none",
                  borderTopLeftRadius: 8, borderBottomLeftRadius: 8,
                  padding: "12px 8px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                  boxShadow: "-4px 0 12px rgba(0,0,0,0.3)", zIndex: 10,
                }}
              >
                <ChatBubbleOutline sx={{ fontSize: "1rem" }} />
                Chat
              </button>
            </Tooltip>
          )}

          <aside
            className="s4y-sidebar"
            style={{
              width: sidebarOpen ? undefined : 0,
              minWidth: sidebarOpen ? undefined : 0,
              overflow: "hidden",
              borderLeft: sidebarOpen ? undefined : "none",
              transition: "width 0.2s ease, min-width 0.2s ease",
            }}
          >
            <div className="s4y-sidebar-tabs" style={{ display: sidebarOpen ? "flex" : "none" }}>
              {([{ id: "chat", label: "CHAT" }, { id: "guests", label: "GUESTS" }, { id: "qr", label: "QR" }] as { id: SidebarTab; label: string }[]).map(({ id, label }) => (
                <button key={id} className={`s4y-sidebar-tab ${activeTab === id ? "active" : ""}`} onClick={() => setActiveTab(id)}>{label}</button>
              ))}
              <Tooltip title="Close panel">
                <IconButton size="small" onClick={() => setSidebarOpen(false)} sx={{ ml: 0.5, color: "rgba(255,255,255,0.5)", "&:hover": { color: "#fff" } }}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>

            {activeTab === "chat" && (
              <div className="s4y-chat-wrap">
                <LiveChat bookingId={bookingId} username={userInfo?.username ?? "host"} height="100%" />
              </div>
            )}
            {activeTab === "guests" && (
              <div className="s4y-chat-wrap s4y-scroll-panel">
                <GuestManagement bookingId={bookingId} />
              </div>
            )}
            {activeTab === "qr" && (
              <div className="s4y-chat-wrap">
                <div className="s4y-qr-panel">
                  <div className="s4y-qr-code-wrap"><QRCodeSVG value={joinUrl} size={200} level="H" includeMargin /></div>
                  <p className="s4y-qr-url">{joinUrl}</p>
                  <button className={`s4y-qr-copy-btn ${copied ? "copied" : ""}`} onClick={handleCopy}>
                    {copied ? <><CheckIcon sx={{ fontSize: "1rem" }} /> Copied!</> : <><ContentCopyIcon sx={{ fontSize: "1rem" }} /> Copy link</>}
                  </button>
                </div>
              </div>
            )}

            <div className="s4y-reactions">
              <div className="s4y-section-label">React</div>
              <div className="s4y-reactions-grid">
                {REACTION_MAP.map(({ emoji, type, label }) => (
                  <button key={emoji} className={`s4y-react-btn${lastSentEmoji === emoji ? " sent" : ""}`} title={label} onClick={() => handleReact(emoji, type)}>
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            <div className="s4y-chat-input-row">
              <input className="s4y-chat-input" placeholder="Say something..." />
              <button className="s4y-chat-send">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
              </button>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

export default Sing4YouCafePage;